<template>
  <el-row :gutter="20">
    <el-col style="text-align: center; background: #f4f4f5; font-size: 14px; padding: 20px">
      网站云实例ID/名称：
      <el-link type="primary" :underline="false" style="margin-top: -5px" :href="getHostUrl()" target="_blank">
        {{ uniqueName }}
        <span v-if="nickName">（{{ nickName }}）</span>
      </el-link>
      <div style="height: 10px" />
      为站点设置自定义域名前，请确认已修改域名 DNS，增加域名 CNAME 解析至：
      <strong>{{ getCName() }}</strong>
      <el-link
        type="primary"
        :underline="false"
        style="margin-top: -5px; margin-left: 10px"
        href="https://sscms.com/docs/v7/host/essential/domain.html"
        target="_blank"
      >
        帮助文档
      </el-link>
    </el-col>
  </el-row>
  <div style="margin: 15px 30px 0">
    <el-alert
      v-if="agentSitesError"
      title="无法访问网站云实例接口，已切换为主站域名绑定模式。绑定会更新网站云访问配置，到期前实例仍可正常使用。"
      type="warning"
      :closable="false"
      show-icon
      style="margin-bottom: 10px"
    ></el-alert>
    <el-table
      :data="sites"
      style="width: 100%"
      row-key="id"
      default-expand-all
      :tree-props="{ children: 'children', hasChildren: 'hasChildren' }"
    >
      <el-table-column prop="siteName" label="站点名称">
        <template #default="scope">
          <el-badge v-if="scope.row.root" type="danger" style="margin-top: 10px" value="主站">
            <el-link :underline="false" type="primary" :href="getSitUrl(scope.row)" target="_blank">
              {{ scope.row.siteName }}
            </el-link>
          </el-badge>
          <el-link :underline="false" type="primary" v-else :href="getSitUrl(scope.row)" target="_blank">
            {{ scope.row.siteName }}
          </el-link>
        </template>
      </el-table-column>
      <el-table-column prop="localUrl" label="站点文件夹" width="120"></el-table-column>
      <el-table-column prop="siteUrl" label="访问地址">
        <template #default="scope">
          <el-link :underline="false" type="primary" :href="getSitUrl(scope.row)" target="_blank">
            {{ getSitUrl(scope.row) }}
          </el-link>
        </template>
      </el-table-column>
      <el-table-column prop="siteUrl" label="自定义域名">
        <template #default="scope">
          {{ getSiteDomain(scope.row) }}
        </template>
      </el-table-column>
      <el-table-column align="center" label="操作" width="100">
        <template #default="scope">
          <el-button-group>
            <el-button @click="btnEditClick(scope.row)">修改</el-button>
          </el-button-group>
        </template>
      </el-table-column>
    </el-table>
  </div>

  <el-dialog title="修改站点域名" v-model="isEdit" width="40%">
    <el-alert v-if="errorMessage" :title="errorMessage" type="error" show-icon style="margin-bottom: 10px"></el-alert>
    <el-form
      v-loading="loading"
      element-loading-text="站点域名设置中，大约需要5到10分钟，请勿关闭页面"
      style="margin: 0 10px"
      ref="form"
      :model="editForm"
      label-position="left"
      label-width="120px"
    >
      <el-form-item label="站点名称">
        <el-badge v-if="site.root" type="danger" style="margin-top: 10px" value="主站">
          <el-link :underline="false" type="primary" :href="getSitUrl(site)" target="_blank">{{ site.siteName }}</el-link>
        </el-badge>
        <el-link :underline="false" type="primary" v-else :href="getSitUrl(site)" target="_blank">{{ site.siteName }}</el-link>
      </el-form-item>
      <el-form-item label="站点文件夹">
        {{ site.localUrl }}
      </el-form-item>
      <el-form-item label="访问地址">
        <el-link :underline="false" type="primary" :href="getSitUrl(site)" target="_blank">{{ getSitUrl(site) }}</el-link>
      </el-form-item>
      <el-form-item label="自定义域名" prop="domain" :rules="[{ validator: validateDomain }]">
        <el-input v-model="editForm.domain" placeholder="请输入自定义域名"></el-input>
        <small class="tips">
          请在此输入你已经拥有的域名
          <br />
          如有多个域名，域名之间使用半角逗号（,）分隔
          <br />
          如需停止使用自定义域名，请删除后更新
        </small>
      </el-form-item>
      <el-form-item label="启用 HTTPS">
        <el-switch v-model="editForm.https"></el-switch>
        <div class="small tips">启用 HTTPS后网站将显示安全标识，提升用户信任感和网站SEO排名</div>
      </el-form-item>
      <el-form-item v-if="site.root" label="启用管理后台">
        <el-switch v-model="editForm.background"></el-switch>
        <div class="small tips">启用管理后台将能够通过自定义域名访问后台管理界面，启用此选项后台地址容易暴露，<el-link :underline="false" type="primary" href="https://sscms.com/docs/v7/host/essential/domain.html" target="_blank">不建议启用</el-link></div>
      </el-form-item>
      <el-divider></el-divider>

      <div class="el-dialog__footer">
        <el-button type="primary" @click="btnEditSubmitClick">确 定</el-button>
        <el-button @click="isEdit = false">取 消</el-button>
      </div>
    </el-form>
  </el-dialog>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import api from '@/api/my/hosts';
import utils from '@/utils';
import { IHostDomain, ISite } from '@/types/models';
import agentSites from '@/api/agent/sites';
import agentSetDomain from '@/api/agent/setDomain';
import { HOST_DOMAIN_HONGKONG, HOST_DOMAIN_MAINLAND } from '@/const';
import { IElForm } from '@/types/element';

interface IEditForm {
  siteId: number;
  prefix: string;
  domain: string;
  https: boolean;
  background: boolean;
}

interface IState {
  hostName: string;
  nodeName: string;
  isHongKong: boolean;
  uniqueName: string;
  nickName: string;
  securityKey: string;
  domains: IHostDomain[];
  sites: ISite[];
  agentSitesError: boolean;
  agentSitesAvailable: boolean;
  isEdit: boolean;
  site: ISite;
  editForm: IEditForm;
  loading: boolean;
  guid: string;
  errorMessage: string;
}

export default defineComponent({
  name: 'Domains',
  props: {
    hostId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      hostName: '',
      nodeName: '',
      isHongKong: false,
      uniqueName: '',
      nickName: '',
      securityKey: '',
      domains: [],
      sites: [],
      agentSitesError: false,
      agentSitesAvailable: true,
      isEdit: false,
      site: {} as ISite,
      editForm: {} as IEditForm,
      loading: false,
      guid: '',
      errorMessage: '',
    } as IState;
  },
  methods: {
    apiGet() {
      api.getDomains(this.hostId, (res) => {
        if (!res) return;
        this.hostName = res.hostName;
        this.nodeName = res.nodeName;
        this.isHongKong = res.isHongKong;
        this.uniqueName = res.uniqueName;
        this.nickName = res.nickName;
        this.securityKey = res.securityKey;
        this.domains = res.domains;

        this.agentSitesError = false;
        this.agentSitesAvailable = true;
        agentSites.getSites(
          this.hostName,
          this.nodeName,
          this.isHongKong,
          {
            securityKey: this.securityKey,
          },
          (res2) => {
            this.sites = res2.sites;
          },
          () => {
            this.agentSitesError = true;
            this.agentSitesAvailable = false;
            this.sites = this.getFallbackSites();
          },
        );
      });
    },

    getFallbackSites(): ISite[] {
      const siteIds = new Set<number>([1]);
      for (const domain of this.domains) {
        siteIds.add(domain.siteId);
      }
      return Array.from(siteIds).map((siteId) => {
        const domain = this.domains.find((x) => x.siteId === siteId);
        const prefix = domain?.prefix || '/';
        return {
          guid: '',
          id: siteId,
          createdDate: new Date(),
          lastModifiedDate: new Date(),
          siteDir: '',
          siteName: siteId === 1 ? '主站' : `站点 ${siteId}`,
          siteType: '',
          imageUrl: '',
          keywords: '',
          description: '',
          tableName: '',
          root: siteId === 1 || prefix === '/',
          parentId: 0,
          taxis: 0,
          localUrl: prefix,
          siteUrl: '',
          isSeparatedWeb: false,
          separatedWebUrl: '',
          children: [],
        };
      });
    },

    apiSetDomain() {
      const siteIds = this.sites.map((x) => x.id);
      this.loading = true;
      api.setDomain(
        {
          hostId: this.hostId,
          siteIds,
          siteId: this.editForm.siteId,
          prefix: this.editForm.prefix,
          domain: this.editForm.domain,
          https: this.editForm.https,
          background: this.editForm.background,
        },
        (res) => {
          this.guid = res.value;
          this.apiSetDomainQuery();
        },
      );
    },

    apiSetDomainQuery() {
      setTimeout(() => {
        api.setDomainQuery(this.guid, (res) => {
          if (res.completed) {
            this.loading = false;
            if (res.success) {
              this.isEdit = false;
              if (this.agentSitesAvailable) {
                this.apiAgentSetDomain();
              } else {
                utils.success('自定义域名设置成功');
              }
              this.apiGet();
            } else {
              this.errorMessage = res.errorMessage;
            }
          } else {
            this.apiSetDomainQuery();
          }
        });
      }, 3000);
    },

    apiAgentSetDomain() {
      let siteDomain = this.editForm.https ? 'https://' : 'http://';
      if (this.editForm.domain.indexOf(',') !== -1) {
        siteDomain += this.editForm.domain.substring(0, this.editForm.domain.indexOf(','));
      } else {
        siteDomain += this.editForm.domain;
      }
      const hostDomain = `https://${this.hostName}${this.nodeName}.${this.isHongKong ? HOST_DOMAIN_HONGKONG : HOST_DOMAIN_MAINLAND}`;
      agentSetDomain.setDomain(
        this.hostName,
        this.nodeName,
        this.isHongKong,
        {
          securityKey: this.securityKey,
          hostDomain,
          siteId: this.editForm.siteId,
          siteDomain,
        },
        (res) => {
          if (res.value) {
            utils.success('自定义域名设置成功');
          }
        },
      );
    },

    validateDomain(rule: string, value: string, callback: (error?: Error) => void) {
      const regexp = /^[a-z0-9]+([-.]{1}[a-z0-9]+)*\.[a-z]{2,6}$/i;
      if (value) {
        if (value.indexOf(':') !== -1) {
          callback(new Error('域名不能包含“:”字符'));
          return;
        }
        if (value.indexOf(' ') !== -1) {
          callback(new Error('域名不能包含空白字符'));
          return;
        }
        const domains = value.split(',');
        for (const domain of domains) {
          if (!regexp.test(domain)) {
            callback(new Error('域名格式不正确'));
            return;
          }
        }
      }
      callback();
    },

    getCName() {
      const hostDomain = this.isHongKong ? HOST_DOMAIN_HONGKONG : HOST_DOMAIN_MAINLAND;
      return `${this.nodeName}.${hostDomain}`;
    },

    getSiteDomain(site: ISite) {
      const domain = this.domains.find((x) => x.siteId === site.id);
      if (domain != null) {
        return domain.domain;
      }
      return '';
    },

    getHostUrl(): string {
      return utils.getHostUrl(this.hostName, this.nodeName, this.isHongKong);
    },

    getSitUrl(site: ISite) {
      return this.getHostUrl() + site.localUrl;
    },

    isHttps(url: string) {
      return !!(url && url.startsWith('https://'));
    },

    getDomain(url: string) {
      const r = /:\/\/(.[^/]+)/;
      const arr = (url || '').match(r);
      return arr && arr.length > 1 ? arr[1] : '';
    },

    btnEditClick(site: ISite) {
      this.errorMessage = '';
      this.isEdit = true;
      this.site = site;
      const settings = this.domains.find((x) => x.siteId === site.id);

      if (settings) {
        this.editForm = {
          siteId: site.id,
          prefix: site.localUrl,
          domain: settings.domain,
          https: settings.https,
          background: settings.background,
        };
      } else {
        const domain = site.isSeparatedWeb ? this.getDomain(site.separatedWebUrl) : '';
        const https = site.isSeparatedWeb ? this.isHttps(site.separatedWebUrl) : true;
        this.editForm = {
          siteId: site.id,
          prefix: site.localUrl,
          domain,
          https,
          background: false,
        };
      }
    },

    btnEditSubmitClick() {
      (this.$refs.form as IElForm).validate((valid) => {
        if (valid) {
          this.errorMessage = '';
          this.apiSetDomain();
        }
      });
    },
  },
  watch: {
    hostId: {
      handler(newVal, oldVal) {
        if (newVal !== oldVal) {
          this.apiGet();
        }
      },
      deep: false,
    },
  },
  mounted() {
    this.apiGet();
  },
});
</script>
